import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import "./style.css";
import ReactECharts from "echarts-for-react";
import TableContainer from "../../../Components/Table";

// Severity to color mapping
const severityColors: Record<number, string> = {
  1: "#F0D89B", // Low
  2: "#DE997E", // Medium
  3: "#D17169", // High
  4: "#CC6561", // Critical
};

const severityLabels: Record<number, string> = {
  1: "Low",
  2: "Medium",
  3: "High",
  4: "Critical",
};

const severityStringToNumber: Record<string, number> = {
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

type TreemapItem = {
  name: string;
  value: number;
  severity: number;
  itemStyle: {
    color: string;
  };
};
interface StateProps {
  findIssueChartData: any;
  selectedAuditId: number;
  handleFindingChartClick: (id: number, severity: string) => void;
  tableLoading: boolean;
  tableData: any;
}

const FindingsAnsIssueCard: React.FC<StateProps> = ({
  findIssueChartData,
  selectedAuditId,
  handleFindingChartClick,
  tableLoading,
  tableData,
}) => {
  const [chartData, setChartData] = useState<TreemapItem[]>([]);

  useEffect(() => {
    if (!findIssueChartData?.heatMap) return;

    const data = findIssueChartData.heatMap.map((item: any, index: number) => {
      const severityStr = item.severity ?? "Critical";
      const severity = severityStringToNumber[severityStr] ?? 4;

      return {
        name: `Question ${item.questionId || index + 1}`,
        value: 1,
        severity,
        itemStyle: {
          color: severityColors[severity] || "#CCCCCC",
        },
      };
    });

    setChartData(data);
  }, [findIssueChartData]);

  const Data = findIssueChartData?.status?.[0] ?? {};
  const FindingsData = findIssueChartData?.totalFindings?.[0] ?? {};

  const statusData = [
    { label: "Open", count: Data.openCount ?? 0, color: "#FFA494" },
    { label: "Under Review", count: Data.underReviewCount ?? 0, color: "#F9E214" },
    { label: "Resolved", count: Data.resolvedCount ?? 0, color: "#67FC6F" },
  ];

  const severityData = [
    { label: "Critical", count: FindingsData.criticalCount ?? 0, color: "#C55356" },
    { label: "High", count: FindingsData.highCount ?? 0, color: "#D17169" },
    { label: "Medium", count: FindingsData.mediumCount ?? 0, color: "#DE997E" },
    { label: "Low", count: FindingsData.lowCount ?? 0, color: "#F0D89B" },
  ];

  const option = {
    animation: false,
    tooltip: { show: false },
    legend: { show: false },
    series: [
      {
        type: "treemap",
        nodeClick: false,
        roam: false,
        breadcrumb: { show: false },
        label: { show: false },
        upperLabel: { show: false },
        itemStyle: {
          color: "#fff",
          borderWidth: 1,
        },
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        data: chartData,
      },
    ],
  };
  const severityCols: Record<string, string> = {
    Critical: "#CC6561",
    High: "#D17169",
    Medium: "#DE997E",
    Low: "#F0D89B",
  };

  const rowDataWithNumbers = tableData?.map((row: any, index: number) => {
    const severity = row.severity || "Unknown";
    const badgeColor = severityCols[severity] || "#999999"; // fallback color

    return {
      No: index + 1,
      ...row,
      severity: (
        <span
          style={{
            backgroundColor: badgeColor,
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            display: "inline-block",
            minWidth: "70px",
            textAlign: "center",
          }}
        >
          {severity}
        </span>
      ),
      satisfied: row.satisfied ? "Yes" : "No",
    };
  });

  return (
    <Card.Body>
      <div className="header-row">
        <h6 className="section-title">Findings and Issues</h6>
      </div>

      <div className="row">
        {/* Severity Summary */}
        <div className="col-6">
          <div className="my-3 overalltitle">Total Findings:</div>
          <div className="severity-cards-container">
            {severityData.map((item, index) => (
              <div
                key={index}
                className="severity-card"
                style={{ backgroundColor: item.color }}
              >
                <div className="severity-label">{item.label}</div>
                <div className="severity-count">{item.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status Summary */}
        <div className="col-6">
          <div className="my-3 overalltitle">Status:</div>
          <div className="severity-cards-container">
            {statusData.map((item, index) => (
              <div
                key={index}
                className="severity-card"
                style={{ backgroundColor: item.color }}
              >
                <div className="severity-label">{item.label}</div>
                <div className="severity-count">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Heatmap Section */}
      <div className="my-3 overalltitle">Severity Distribution:</div>
      <div style={{ width: "100%" }}>
        <ReactECharts
          option={option}
          style={{ width: "100%" }}
          onEvents={{
            click: (params: any) => {
              const severityNum = params?.data?.severity;
              const severityLabel = severityLabels[severityNum] || "Unknown";
              handleFindingChartClick(selectedAuditId, severityLabel);
            },
          }}
        />
      </div>
      <div className="my-3">
        <TableContainer
          columnData={[
            { key: "No", label: "No", alignText: "center" },
            { key: "question", label: "Question", alignText: "start", tdClassname: "wraptableresponse" },
            { key: "severity", label: "Severity", alignText: "center" },
            { key: "response", label: "Reponse", alignText: "start", tdClassname: "wraptableresponse" },
            { key: "satisfied", label: "Meets Criteria", alignText: "center" },
          ]}
          rowData={rowDataWithNumbers}
          totalResult={0}
          title={"Issue Tracking:"}
          enableActionButton={false}
          enableMoreFeilds={false}
          enableFilterButton={false}
          enableCheckbox={false}
          enableFilter={false}
          itemsPerPage={10}
          currentPage={0}
          setCurrentPage={() => null}
          checkBox={false}
          onPageLimitChange={() => { /* noop */ }}
          onPageNoChange={() => { /* noop */ }}
          showLoading={tableLoading}
        />
      </div>
    </Card.Body>
  );
};

export default FindingsAnsIssueCard;
